import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import useRazorpay from '../hooks/useRazorpay';
import { poojaTypesData, getPoojaById } from '../config/poojaConfig';
import './BookingForm.css';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getRazorpayMethodConfig, getRazorpayUpiPrefill, isValidUpiId } from '../utils/razorpay';
import { useLanguage } from '../contexts/LanguageContext';

const fallbackTemples = [
  { key: 'varanasi', value: 'varanasi', label: 'Kashi Vishwanath Temple, Varanasi' },
  { key: 'jagannath', value: 'jagannath', label: 'Jagannath Temple, Puri' },
  { key: 'sidhhaMahavir', value: 'Sidhha Mahavir', label: 'Sidhha Mahavir Temple, Puri' },
  { key: 'navaGraha', value: 'Nava Graha', label: 'Nava Graha Temple, Puri' },
  { key: 'loknath', value: 'Loknath', label: 'Lokanath Temple, Puri' },
  { key: 'mangala', value: 'Mangala', label: 'Bata Mangala Temple, Puri' },
  { key: 'cuttackChandi', value: 'Cuttack Chandi', label: 'Chandi Temple, Cuttack' },
  { key: 'ugraTara', value: 'Ugra Tara', label: 'Ugra Tara Temple, Khordha' },
  { key: 'tarini', value: 'Tarini', label: 'Tarini Temple' },
  { key: 'mahavir', value: 'Mahavir', label: 'Shiruli Mahavir Temple, Puri' },
  { key: 'mangalnath', value: 'mangalnath', label: 'Mangalnath Temple, Ujjain' },
  { key: 'lakshmi', value: 'Lakshmi', label: 'Lakshmi Temple, Puri' },
  { key: 'vimala', value: 'Vimala', label: 'Vimala Temple, Puri' },
  { key: 'bhubaneswari', value: 'Bhubaneswari', label: 'Bhubaneswari Temple, Bhubaneswar' },
  { key: 'bagalamukhi', value: 'bagalamukhi', label: 'Maa Bagalamukhi Temple, Puri' },
  { key: 'lingaraj', value: 'lingaraj', label: 'Lingaraj Temple, Bhubaneswar' }
];

const toTempleOption = (temple) => ({
  key: temple.templeId || temple.value,
  value: temple.templeId || temple.value,
  label: temple.name || temple.label
});

const BookingForm = ({ selectedPooja, poojaList = poojaTypesData }) => {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useAuth();
  const { t } = useLanguage();
  useRazorpay(); // Loads the Razorpay script when the component mounts

  const [formData, setFormData] = useState({
    poojaType: '',
    temple: '',
    poojaDate: '',
    poojaTime: '',
    devoteeName: '',
    gotra: '', // <-- Added Gotra to state
    email: '',
    phone: '',
    upiId: '',
    specialRequests: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Pre-fill form when a pooja is selected or user data is available
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      poojaType: selectedPooja ? selectedPooja.id : prev.poojaType,
      devoteeName: user?.fullName || prev.devoteeName,
      email: user?.email || prev.email,
      phone: user?.phone || prev.phone,
    }));
  }, [selectedPooja, user]);

  // Helper function to calculate the minimum allowed date (Now + 48 hours)
  const getMinAllowedDate = () => {
    const minDate = new Date();
    minDate.setHours(minDate.getHours() + 48);
    
    // Format as YYYY-MM-DD in local time
    const year = minDate.getFullYear();
    const month = String(minDate.getMonth() + 1).padStart(2, '0');
    const day = String(minDate.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  // Set minimum date for date picker
  useEffect(() => {
    // Set minimum date to current time + 48 hours
    const minDateStr = getMinAllowedDate();
    const dateInput = document.getElementById('pooja-date');
    if (dateInput) {
      dateInput.min = minDateStr;
    }
  }, []);

  const poojaTypesOptions = poojaList.map((p) => ({
    value: p.id,
    label: `${t(`onlinePooja.poojaTypes.items.${p.id}.title`, {}, p.title)} - ₹${p.price}`
  }));

  const selectedService = poojaList.find((pooja) => pooja.id === formData.poojaType);
  const hasCatalogueTempleData = selectedService && Array.isArray(selectedService.availableTemples);
  const serviceTempleOptions = hasCatalogueTempleData
    ? selectedService.availableTemples.map(toTempleOption)
    : fallbackTemples;
  const temples = serviceTempleOptions;

  useEffect(() => {
    if (!formData.temple) return;
    const isTempleStillAvailable = temples.some((temple) => temple.value === formData.temple);
    if (!isTempleStillAvailable) {
      setFormData((prev) => ({ ...prev, temple: '' }));
    }
  }, [formData.temple, temples]);

  const timeSlots = [
    { value: '06:00', key: '06_00', label: '6:00 AM' },
    { value: '08:00', key: '08_00', label: '8:00 AM' },
    { value: '10:00', key: '10_00', label: '10:00 AM' },
    { value: '12:00', key: '12_00', label: '12:00 PM' },
    { value: '14:00', key: '14_00', label: '2:00 PM' },
    { value: '16:00', key: '16_00', label: '4:00 PM' },
    { value: '18:00', key: '18_00', label: '6:00 PM' },
    { value: '20:00', key: '20_00', label: '8:00 PM' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: value 
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.poojaType) newErrors.poojaType = t('onlinePooja.bookingForm.errors.selectPoojaType');
    if (!formData.temple) newErrors.temple = t('onlinePooja.bookingForm.errors.selectTemple');
    
    // --- UPDATED 48 HOUR DATE VALIDATION ---
    if (!formData.poojaDate) {
      newErrors.poojaDate = t('onlinePooja.bookingForm.errors.selectDate', {}, 'Please select a date');
    } else {
      // Ensure users didn't manually type a date earlier than 48 hours
      const selectedDate = new Date(formData.poojaDate);
      selectedDate.setHours(0, 0, 0, 0); // Normalize time
      
      const minAllowed = new Date(getMinAllowedDate());
      minAllowed.setHours(0, 0, 0, 0);

      if (selectedDate < minAllowed) {
        newErrors.poojaDate = t('onlinePooja.bookingForm.errors.dateTooEarly', {}, 'Booking date must be at least 48 hours from now');
      }
    }

    if (!formData.poojaTime) newErrors.poojaTime = t('onlinePooja.bookingForm.errors.selectTime');
    if (!formData.devoteeName.trim()) newErrors.devoteeName = t('onlinePooja.bookingForm.errors.enterName');
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = t('onlinePooja.bookingForm.errors.validEmail');
    if (!formData.phone.trim() || !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) newErrors.phone = t('onlinePooja.bookingForm.errors.validPhone');
    if (!isValidUpiId(formData.upiId)) newErrors.upiId = t('onlinePooja.bookingForm.errors.validUpi');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (!isLoggedIn) {
      toast.error(t('onlinePooja.bookingForm.toasts.loginRequired'));
      return;
    }
    setIsSubmitting(true);
    try {
      const bookingResponse = await apiService.createBooking({
        devoteeName: formData.devoteeName, 
        gotra: formData.gotra, // <-- Added Gotra to API payload
        email: formData.email, 
        phone: formData.phone,
        poojaType: formData.poojaType, 
        temple: formData.temple, 
        poojaDate: formData.poojaDate,
        poojaTime: formData.poojaTime, 
        specialRequests: formData.specialRequests,
      });

      if (bookingResponse.success) {
        initiatePayment(bookingResponse.data);
      } else {
        throw new Error(bookingResponse.message || t('onlinePooja.bookingForm.toasts.bookingCreationFailed'));
      }
    } catch (error) {
      console.error('Booking submission error:', error);
      setIsSubmitting(false);
      toast.error(error.message);
    }
  };

  const initiatePayment = async (bookingData) => {
    const poojaDetails = poojaList.find((pooja) => pooja.id === bookingData.poojaType) || getPoojaById(bookingData.poojaType);
    const localizedPoojaTitle = poojaDetails
      ? t(`onlinePooja.poojaTypes.items.${poojaDetails.id}.title`, {}, poojaDetails.title)
      : '';
    if (!poojaDetails) {
      toast.error(t('onlinePooja.bookingForm.toasts.poojaDetailsMissing'));
      setIsSubmitting(false);
      return;
    }

    try {
      const orderResponse = await apiService.createRazorpayOrder({
        amount: bookingData.amount,
        currency: 'INR',
        entityId: bookingData.bookingId,
        entityType: 'booking'
      });

      if (!orderResponse.success) {
        throw new Error(orderResponse.message || t('onlinePooja.bookingForm.toasts.orderCreationFailed'));
      }

      const order = orderResponse.data;
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: 'Anandmaya',
        description: t(
          'onlinePooja.bookingForm.paymentDescription',
          { title: localizedPoojaTitle },
          `Payment for ${localizedPoojaTitle}`
        ),
        image: '/images/logo.png',
        order_id: order.id,
        handler: async function (response) {
          try {
            const verificationResponse = await apiService.verifyRazorpayPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              entityId: bookingData.bookingId, 
              entityType: 'booking'             
            });

            if (verificationResponse.success) {
              toast.success(
                (toastInstance) => (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {t('onlinePooja.bookingForm.toasts.bookingConfirmed')}
                    <button
                      style={{ background: '#8B4513', color: 'white', padding: '5px 10px', borderRadius: '5px', border: 'none', cursor: 'pointer' }}
                      onClick={() => {
                        navigate('/dashboard/bookings'); 
                        toast.dismiss(toastInstance.id);
                      }}
                    >
                      {t('onlinePooja.bookingForm.toasts.viewBookings')}
                    </button>
                  </span>
                ), {
                icon: '🙏',
                duration: 6000, 
              }
              );
              setFormData({ 
                poojaType: '', temple: '', poojaDate: '', poojaTime: '', devoteeName: '', gotra: '', email: '', phone: '', upiId: '', specialRequests: ''
              });
            } else {
              toast.error(verificationResponse.message || t('onlinePooja.bookingForm.toasts.paymentVerificationFailed'));
            }
          } catch (verifyError) {
            toast.error(verifyError.message);
          } finally {
            setIsSubmitting(false);
          }
        },
        method: getRazorpayMethodConfig(),
        prefill: getRazorpayUpiPrefill({
          name: formData.devoteeName,
          email: formData.email,
          contact: formData.phone,
        }, formData.upiId),
        notes: {
          bookingId: bookingData.bookingId, 
        },
        theme: { color: '#8B4513' },
        modal: {
          ondismiss: function () {
            toast.error(t('onlinePooja.bookingForm.toasts.paymentCancelled'));
            setIsSubmitting(false);
          },
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (paymentError) {
      console.error('Payment initiation error:', paymentError);
      setIsSubmitting(false);
      toast.error(paymentError.message);
    }
  };


  return (
    <section className="booking-form-section">
      <div className="container">
        <div className="section-header">
          <h2>{t('onlinePooja.bookingForm.title')}</h2>
          <p>{t('onlinePooja.bookingForm.subtitle')}</p>
        </div>
        <div className="booking-form-container">
          <motion.form
            className="booking-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            {/* Pooja Type in its own full-width row */}
            <div className="form-group">
              <label htmlFor="pooja-type">{t('onlinePooja.bookingForm.labels.poojaType')}</label>
              <select
                id="pooja-type"
                name="poojaType"
                value={formData.poojaType}
                onChange={handleChange}
                className={errors.poojaType ? 'error' : ''}
              >
                <option value="">{t('onlinePooja.bookingForm.placeholders.choosePoojaType')}</option>
                {poojaTypesOptions.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.poojaType && <span className="error-message">{errors.poojaType}</span>}
            </div>

            {/* Temple in its own full-width row */}
            <div className="form-group">
              <label htmlFor="temple">{t('onlinePooja.bookingForm.labels.temple')}</label>
              <select
                id="temple"
                name="temple"
                value={formData.temple}
                onChange={handleChange}
                className={errors.temple ? 'error' : ''}
              >
                <option value="">{t('onlinePooja.bookingForm.placeholders.chooseTemple')}</option>
                {temples.map(temple => (
                  <option key={temple.value} value={temple.value}>
                    {t(`onlinePooja.bookingForm.temples.${temple.key}`, {}, temple.label)}
                  </option>
                ))}
              </select>
              {errors.temple && <span className="error-message">{errors.temple}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="pooja-date">{t('onlinePooja.bookingForm.labels.preferredDate')}</label>
                <input
                  type="date"
                  id="pooja-date"
                  name="poojaDate"
                  value={formData.poojaDate}
                  onChange={handleChange}
                  className={errors.poojaDate ? 'error' : ''}
                />
                {errors.poojaDate && <span className="error-message">{errors.poojaDate}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="pooja-time">{t('onlinePooja.bookingForm.labels.preferredTime')}</label>
                <select
                  id="pooja-time"
                  name="poojaTime"
                  value={formData.poojaTime}
                  onChange={handleChange}
                  className={errors.poojaTime ? 'error' : ''}
                >
                  <option value="">{t('onlinePooja.bookingForm.placeholders.selectTime')}</option>
                  {timeSlots.map(slot => (
                    <option key={slot.value} value={slot.value}>
                      {t(`onlinePooja.bookingForm.timeSlots.${slot.key}`, {}, slot.label)}
                    </option>
                  ))}
                </select>
                {errors.poojaTime && <span className="error-message">{errors.poojaTime}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="devotee-name">{t('onlinePooja.bookingForm.labels.devoteeName')}</label>
                <input
                  type="text"
                  id="devotee-name"
                  name="devoteeName"
                  placeholder={t('onlinePooja.bookingForm.placeholders.devoteeName')}
                  value={formData.devoteeName}
                  onChange={handleChange}
                  className={errors.devoteeName ? 'error' : ''}
                />
                {errors.devoteeName && <span className="error-message">{errors.devoteeName}</span>}
              </div>
              
              {/* --- NEW GOTRA FIELD --- */}
              <div className="form-group">
                <label htmlFor="gotra">{t('onlinePooja.bookingForm.labels.gotra', {}, 'Gotra (Optional)')}</label>
                <input
                  type="text"
                  id="gotra"
                  name="gotra"
                  placeholder={t('onlinePooja.bookingForm.placeholders.gotra', {}, 'Enter your Gotra')}
                  value={formData.gotra}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">{t('onlinePooja.bookingForm.labels.email')}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder={t('onlinePooja.bookingForm.placeholders.email')}
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="phone">{t('onlinePooja.bookingForm.labels.phone')}</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder={t('onlinePooja.bookingForm.placeholders.phone')}
                  value={formData.phone}
                  onChange={handleChange}
                  className={errors.phone ? 'error' : ''}
                />
                {errors.phone && <span className="error-message">{errors.phone}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="upi-id">{t('onlinePooja.bookingForm.labels.upiId')}</label>
              <input
                type="text"
                id="upi-id"
                name="upiId"
                placeholder={t('onlinePooja.bookingForm.placeholders.upiId')}
                value={formData.upiId}
                onChange={handleChange}
                className={errors.upiId ? 'error' : ''}
              />
              {errors.upiId && <span className="error-message">{errors.upiId}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="special-requests">{t('onlinePooja.bookingForm.labels.specialRequests')}</label>
              <textarea
                id="special-requests"
                name="specialRequests"
                placeholder={t('onlinePooja.bookingForm.placeholders.specialRequests')}
                rows="4"
                value={formData.specialRequests}
                onChange={handleChange}
              ></textarea>
            </div>
            
            <motion.button
              type="submit"
              className="btn btn-primary btn-large"
              disabled={isSubmitting}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> {t('onlinePooja.bookingForm.submit.processing')}
                </>
              ) : (
                t('onlinePooja.bookingForm.submit.proceedToPayment')
              )}
            </motion.button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default BookingForm;
